<template>
    <VJoyFunnel>
        <template #funnel-header>
            <VJoyFunnelHeader>
                <template #funnel-header-left>
                    <VJoyIconButton
                        icon="cross"
                        size="small"
                        variant="ghost"
                        data-testid="expense-funnel_close-button"
                        @click="openBatchAbortDialog('close')"
                    />
                </template>
            </VJoyFunnelHeader>
        </template>
        <template #funnel-stepper>
            <VJoyStepper :step="validationStep">
                <VJoyStep>
                    {{ t('projectclient.expenses.validation.page.title', {nbExpenses: formattedExpensesList.length}) }}
                </VJoyStep>
                <VJoyStep>{{ t('projectclient.expenses.validation.navstep.confirm') }}</VJoyStep>
            </VJoyStepper>
        </template>
        <VJoyTemplate>
            <template #template-title>
                <h2>
                    {{
                        validationStep === 1
                            ? t('projectclient.expenses.validation.page.title', {nbExpenses: formattedExpensesList.length})
                            : t('projectclient.expenses.validation.navstep.confirm')
                    }}
                    <VJoyBadge
                        variant="info"
                        :label="`${nbOfExpenses} ${t('projectclient.expenses.validation.table.column.action.badge', {
                            nbExpenses: nbOfExpenses,
                        })}`"
                    />
                </h2>
            </template>
            <template #template-main>
                <form :id="formId">
                    <ExpensesTable v-if="validationStep === 1" />
                    <BillingInformation
                        v-else
                        v-model="clientCompanyBillingValidationData"
                        :r$="r$"
                        :form-id="formId"
                        @billing-information-updated-successfully="onBillingInformationUpdatedSuccessfully"
                        @billing-information-update-failed="onBillingInformationUpdateFailed"
                        @billing-information-filled-state-changed="onBillingInformationFilledStateChanged"
                    />
                    <ComplianceNote
                        :freelancer="projectInformation.freelancer.fullName"
                        :conversation-id="projectInformation.contract.conversationId"
                    />
                </form>
                <VJoyFunnelFooter v-if="validationStep === 1">
                    <template #footer-right>
                        <VJoyButton
                            v-if="isParticipant"
                            :label="t('projectclient.expenses.page.button.approve')"
                            size="small"
                            variant="main"
                            data-testid="expense-validationNext"
                            @click="navigateForward()"
                        />
                        <VJoyButton
                            v-else
                            :label="t('projectclient.expenses.page.button.approve')"
                            size="small"
                            variant="main"
                            data-testid="expense-validationNext"
                            :disabled="!hasAuthorityToValidateExpenses"
                            @click="openAdminValidationDialog()"
                        />
                    </template>
                </VJoyFunnelFooter>
                <VJoyFunnelFooter v-else-if="validationStep === 2">
                    <template #footer-left>
                        <VJoyButton
                            :label="t('projectclient.expenses.page.button.back')"
                            size="small"
                            variant="secondary"
                            @click="openBatchAbortDialog('back')"
                        />
                    </template>
                    <template #footer-right>
                        <VJoyButton
                            v-if="isParticipant"
                            :label="
                                isPunchoutEnabled
                                    ? t('projectclient.expenses.page.button.send.punchout', [punchoutDetails?.procurementAppName])
                                    : t('projectclient.expenses.page.button.send')
                            "
                            size="small"
                            variant="main"
                            :loading="updatingClientCompanyBillingInformation"
                            data-testid="expense-validationConfirm"
                            :disabled="!billingInformationFilled"
                            @click="saveBillingInformation"
                        />
                        <VJoyButton
                            v-else
                            :label="
                                isPunchoutEnabled
                                    ? t('projectclient.expenses.page.button.send.punchout', [punchoutDetails?.procurementAppName])
                                    : t('projectclient.expenses.page.button.send')
                            "
                            size="small"
                            variant="main"
                            data-testid="expense-validationConfirm-admin"
                            :disabled="!hasAuthorityToValidateExpenses"
                            @click="openAdminValidationDialog"
                        />
                    </template>
                </VJoyFunnelFooter>
            </template>
            <template #template-sidebar>
                <ExpensesSummary />
            </template>
        </VJoyTemplate>
    </VJoyFunnel>

    <ClientOpsConfirmationModal
        v-if="isClientOpsDialogVisible"
        v-model="nonParticipantValidationComment"
        :header="t('projectclient.expenses.page.non.participant.dialog.header')"
        :message="t('projectclient.expenses.page.non.participant.dialog.body')"
        :is-updating="updatingClientCompanyBillingInformation"
        @confirm:action="validationStep === 1 ? navigateForward() : confirmBillingInformation()"
        @close:dialog="handleShowDialog"
    />
    <AbortBatchCreationDialog
        v-if="isAbortDialogOpen && abortOperation"
        :origin="abortOperation"
        @close:dialog="closeBatchAbortDialog"
        @confirm:action="confirmBatchAbortion"
    />
    <LoginToERPDialog
        v-if="isLoginToERPDialogOpen"
        :erp-name="punchoutDetails?.procurementAppName!!"
        :erp-url="punchoutDetails?.procurementAppUrl!!"
        :project-url="expenseUrl"
        @close:dialog="closeLoginToERPDialog"
    />
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {useClientOpsModal, useProjectClientRedirect, useTrackPurchaseOrder} from '@common/composables';
    import {useProjectInformationStore, useUserStore} from '@common/stores';
    import {ClientOpsConfirmationModal} from '@common/ui';
    import {navigateTo} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useAsyncData, useMFEvent, useRoute, useRouter, useTranslation} from '#imports';
    import {timeout} from '@malt/utils';
    import {
        pushVJoySnackbar,
        VJoyBadge,
        VJoyButton,
        VJoyIconButton,
        VJoyFunnel,
        VJoyFunnelFooter,
        VJoyFunnelHeader,
        VJoyStep,
        VJoyStepper,
        VJoyTemplate,
    } from '@maltjoy/core-vue';
    import type {ProjectValidationParam} from '@api';
    import {useClientCompanyBillingInfosValidationForm} from '~/features';
    import {useClientCompanyBillingInfosStore, useExpenseValidationStore} from '../stores';
    import type {ExpenseClientCompanyBillingInfoParam, FunnelOrigin} from '../types';
    import {clientCompanyInfosInitData} from '../utils';
    import AbortBatchCreationDialog from './AbortBatchCreationDialog.vue';
    import BillingInformation from './BillingInformation/BillingInformation.vue';
    import ComplianceNote from './ComplianceNote.vue';
    import ExpensesSummary from './ExpensesSummary/ExpensesSummary.vue';
    import ExpensesTable from './ExpensesTable.vue';
    import LoginToERPDialog from './LoginToERPDialog.vue';

    const {t} = useTranslation();
    const router = useRouter();
    const route = useRoute();

    const {trackPurchaseOrderAdded} = useTrackPurchaseOrder();

    // User roles
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    // Funnel navigation
    const validationStep = ref(router.currentRoute.value.query.batch ? 2 : 1);

    async function navigateForward() {
        try {
            await createBatch();
            validationStep.value = 2;
        } catch (e) {
            validationStep.value = 1;
        }
    }

    function navigateBack() {
        validationStep.value = 1;
    }

    const billingInformationFilled = ref(false);

    // projectInformation store
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    // clientCompanyBillingInformation store
    const clientCompanyBillingInformationStore = useClientCompanyBillingInfosStore();
    const {updatingClientCompanyBillingInformation, clientCompanyBillingInformation} = storeToRefs(clientCompanyBillingInformationStore);

    // expenseValidation store
    const expenseValidationStore = useExpenseValidationStore();
    const {batchExpenseItem, formattedExpensesList, tempBatchId} = storeToRefs(expenseValidationStore);

    // Punchout
    const punchoutDetails = computed(() => batchExpenseItem.value?.punchoutDetails);
    const isPunchoutEnabled = computed(() => punchoutDetails.value?.isPunchoutEnabled);
    const expenseUrl = computed(() => window.location.href);
    const nbOfExpenses = computed(() => batchExpenseItem.value?.nbExpenseInBatch ?? 0);

    const isLoginToERPDialogOpen = ref(false);

    async function submitToERP() {
        if (punchoutDetails.value?.isPunchoutSessionActive) {
            await sendExpensesToPunchout();
            isLoginToERPDialogOpen.value = false;
        } else {
            isLoginToERPDialogOpen.value = true;
        }
    }

    async function sendExpensesToPunchout() {
        const redirectionUrl = await expenseValidationStore.sendExpensesToPunchout({
            projectId: projectInformation.value.projectId,
            expenseBatchId: router.currentRoute.value.query.batch as string,
            sendExpensesToPunchoutRequest: {
                comment: nonParticipantValidationComment.value,
            },
        });

        if (redirectionUrl != null) {
            window.location.href = redirectionUrl;
        }
    }

    function closeLoginToERPDialog() {
        isLoginToERPDialogOpen.value = false;
    }

    const {originUrl} = useProjectClientRedirect('expense-approved');

    // Billing Information form
    const clientCompanyBillingValidationData = ref<ExpenseClientCompanyBillingInfoParam & Partial<ProjectValidationParam>>(
        clientCompanyInfosInitData(clientCompanyBillingInformation.value) as ExpenseClientCompanyBillingInfoParam,
    );

    const {validateForm, formId, r$} = useClientCompanyBillingInfosValidationForm(clientCompanyBillingValidationData);

    const onBillingInformationUpdatedSuccessfully = async () => {
        await confirmBillingInformation();
    };

    const onBillingInformationUpdateFailed = () => {
        pushVJoySnackbar({
            props: {
                header: t('projectclient.billinginformation.invoice.billing.information.error.header'),
                message: t('projectclient.billinginformation.invoice.billing.information.error.message', [projectInformation.value.client.companyId]),
                level: 'error',
            },
        });
    };
    const onBillingInformationFilledStateChanged = (state: boolean) => {
        billingInformationFilled.value = state;
    };

    function showPurchaseOrderRequiredSnackbar() {
        pushVJoySnackbar({
            props: {
                message: t('projectclient.expenses.validation.error.purchaseOrderRequired'),
                level: 'warning',
                duration: 'forever',
            },
        });
    }

    // Validate expense(s)
    async function validateFreelancerExpenseBatch(projectId: string) {
        await expenseValidationStore.validateExpensesAsBatch({
            projectId,
            expenseBatchId: route.query.batch as string,
            expenseBatchApprovedRequest: {
                clientPurchaseOrder: clientCompanyBillingValidationData.value.purchaseOrderNumber,
                comment: nonParticipantValidationComment.value,
            },
        });
        await timeout(3000);
        if (route.query.origin === 'project-view') {
            navigateTo(`/project/${projectInformation.value.projectId}/activity`);
        } else {
            navigateTo(originUrl.value);
        }
    }

    // handle admin validation + clientOpsModal
    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToValidateExpenses = computed(() => roles.value.includes('ROLE_EXPENSE_VALIDATION'));

    const {nonParticipantValidationComment, isClientOpsDialogVisible, handleShowDialog} = useClientOpsModal();

    // explanation is needed for both batch creation + approval. If comment is already collected on approvel, we don't show dialog again
    async function openAdminValidationDialog() {
        if (validationStep.value === 1) {
            isClientOpsDialogVisible.value = true;
        } else if (validationStep.value === 2) {
            const isValid = await validateForm();
            if (isValid) {
                isClientOpsDialogVisible.value = true;
            } else if (r$.purchaseOrderNumber?.$error) {
                showPurchaseOrderRequiredSnackbar();
            }
        }
    }

    const {dispatchMFEvent} = useMFEvent('compliance-mf-billing-information');
    const saveBillingInformation = async () => {
        dispatchMFEvent('trigger-save');
    };

    async function confirmBillingInformation() {
        const isValid = await validateForm();

        if (!isValid) {
            if (r$.purchaseOrderNumber?.$error) {
                showPurchaseOrderRequiredSnackbar();
            }
            return;
        }

        if (isPunchoutEnabled.value) {
            await submitToERP();
        } else {
            const submittedPurchaseOrderNumber = clientCompanyBillingValidationData.value.purchaseOrderNumber?.trim();
            const {projectId} = projectInformation.value;
            await validateFreelancerExpenseBatch(projectId);

            // The expense form never prefills the purchase order, so any submitted value is an addition
            if (submittedPurchaseOrderNumber) {
                await trackPurchaseOrderAdded('expense_approval_page', batchExpenseItem.value.clientPurchaseOrderRequired);
            }
        }
    }

    // handle everything related to batch creation + deletion
    useAsyncData(
        async () => {
            if (router.currentRoute.value.query.batch) {
                try {
                    await expenseValidationStore.fetchExpenseBatch({
                        projectId: projectInformation.value.projectId,
                        expenseBatchId: router.currentRoute.value.query.batch as string,
                    });
                } catch (e) {
                    await navigateTo('/');
                }
            }
        },
        {deep: true},
    );

    async function createBatch() {
        await expenseValidationStore.createExpenseBatch({
            projectId: projectInformation.value.projectId,
            expenseCreateBatchRequest: {
                expenseIds: formattedExpensesList.value.map((e) => e.id),
                comment: nonParticipantValidationComment.value,
            },
        });
        if (tempBatchId.value) {
            router.replace({path: route.path, query: {...route.query, batch: tempBatchId.value}});
        }
    }

    const abortOperation = ref<FunnelOrigin | null>(null);
    const isAbortDialogOpen = ref(false);

    function closeBatchAbortDialog() {
        isAbortDialogOpen.value = false;
        abortOperation.value = null;
    }

    function openBatchAbortDialog(origin: FunnelOrigin) {
        abortOperation.value = origin;
        isAbortDialogOpen.value = true;
    }

    async function confirmBatchAbortion() {
        if (abortOperation.value === 'close') {
            closeBatchAbortDialog();
            await expenseValidationStore.fetchExpensesSubmitted(projectInformation.value.projectId);
            navigateTo(originUrl.value);
        } else if (abortOperation.value === 'back') {
            await expenseValidationStore.abortCreateExpenseBatch({
                projectId: projectInformation.value.projectId,
                expenseBatchId: route.query.batch as string,
            });
            closeBatchAbortDialog();
            router.replace({path: route.path});
            navigateBack();
        }
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text';

    .joy-funnel {
        --funnel-background-color: var(--joy-color-neutral-10);
    }

    // DS overrides for page heading
    :deep(.joy-template__heading) {
        margin-bottom: var(--joy-core-spacing-4);
    }

    :deep(.joy-template-slot-title) {
        font-weight: var(--joy-font-weight-bold) !important;
        font-size: var(--joy-font-size-primary-800) !important;
    }
</style>
